import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Loader2, Pencil, Plus, ShieldCheck, Trash2 } from 'lucide-react';
import {
  defiEnrolementCle,
  enrolerCle,
  fetchMesCles,
  renommerCle,
  retirerMaCle,
  type CleSecurite,
} from '../services/api';
import { clesPossibles, creerCle, type DefiEnrolement } from '../utils/cleSecurite';

/**
 * Mes clés de sécurité.
 *
 * CE QU'ELLE APPORTE, ET QUE LE CODE À SIX CHIFFRES N'APPORTE PAS
 * ---------------------------------------------------------------------------
 * L'écran le dit en une phrase, parce que c'est la seule raison d'ajouter un
 * geste à sa connexion : un code se recopie sur une page qui imite la nôtre,
 * une clé refuse de signer pour un domaine qui n'est pas le sien.
 *
 * Dit autrement — et c'est ainsi que l'écran le dit — la clé ferme
 * l'hameçonnage au lieu de le ralentir.
 *
 * DEUX CLÉS, ET L'ÉCRAN Y REVIENT
 * ---------------------------------------------------------------------------
 * Une clé unique perdue, c'est un compte fermé. Le message qui suit le premier
 * enrôlement le dit, et le bloc le redit tant qu'il n'y en a qu'une : c'est le
 * moment où l'on est le plus disposé à en brancher une seconde.
 */
export default function MesClesSecurite() {
  const client = useQueryClient();
  const [message, setMessage] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const possible = clesPossibles();

  const liste = useQuery({
    queryKey: ['mes-cles'],
    queryFn: fetchMesCles,
    enabled: possible,
  });

  const rafraichir = () => {
    void client.invalidateQueries({ queryKey: ['mes-cles'] });
  };

  const renommage = useMutation({
    mutationFn: ({ id, nom }: { id: number; nom: string }) => renommerCle(id, nom),
    onSuccess: rafraichir,
    onError: (e: Error) => setErreur(e.message),
  });

  const retrait = useMutation({
    mutationFn: (id: number) => retirerMaCle(id),
    onSuccess: (r: { message?: string }) => {
      setMessage(r.message ?? null);
      rafraichir();
    },
    onError: (e: Error) => setErreur(e.message),
  });

  /**
   * L'enrôlement, en trois temps : demander un défi, toucher la clé, envoyer.
   *
   * Écrit à la main plutôt qu'en mutation react-query : le geste physique se
   * place ENTRE deux appels réseau, et une mutation qui enveloppe les trois
   * rendrait l'état d'attente faux — « en cours » pendant que l'utilisateur
   * cherche sa clé dans son sac.
   */
  const enroler = async () => {
    setErreur(null);
    setMessage(null);
    setEnCours(true);

    try {
      const defi = (await defiEnrolementCle()) as DefiEnrolement & { success: boolean };
      const creee = await creerCle(defi);
      const reponse = (await enrolerCle(creee)) as { success?: boolean; message?: string };

      setMessage(reponse.message ?? 'Clé enregistrée.');
      rafraichir();
    } catch (e) {
      const echec = e as Error & { name?: string };

      /*
       * Deux échecs très différents, et le même message serait trompeur.
       *
       * « NotAllowedError » veut dire que personne n'a touché la clé — délai
       * dépassé, ou refus. Ce n'est pas une panne, et le dire comme une panne
       * ferait chercher un problème qui n'existe pas.
       */
      setErreur(
        echec.name === 'NotAllowedError'
          ? 'Aucune clé n’a été touchée. Rebranchez-la et recommencez.'
          : (echec.message || 'L’enrôlement a échoué.'),
      );
    } finally {
      setEnCours(false);
    }
  };

  if (!possible) {
    return (
      <section className="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <h3 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-gray-400" />
          Clé de sécurité
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Ce navigateur ne sait pas dialoguer avec une clé de sécurité, ou la page n’est pas servie
          en HTTPS. Essayez depuis un navigateur à jour.
        </p>
      </section>
    );
  }

  const cles: CleSecurite[] = liste.data?.cles ?? [];

  return (
    <section className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
      <header>
        <h3 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-primary-600" />
          Clé de sécurité
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Une clé branchée en USB, ou approchée du téléphone, remplace le code à six chiffres. Elle
          a un avantage qu’aucun code n’a : <strong>elle refuse de signer pour un autre site</strong>.
          Une page qui imiterait la nôtre obtiendrait votre mot de passe et votre code — jamais sa
          signature.
        </p>
      </header>

      {message !== null && (
        <p className="rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900 px-3 py-2 text-sm text-green-800 dark:text-green-300">
          {message}
        </p>
      )}
      {erreur !== null && (
        <p
          role="alert"
          className="rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 px-3 py-2 text-sm text-red-800 dark:text-red-300"
        >
          {erreur}
        </p>
      )}

      {liste.isPending ? (
        <div className="flex justify-center py-4">
          <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
        </div>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-gray-800">
          {cles.map((cle) => (
            <LigneCle
              key={cle.id}
              cle={cle}
              occupe={renommage.isPending || retrait.isPending}
              surRenommer={(nom) => renommage.mutate({ id: cle.id, nom })}
              surRetirer={() => {
                if (
                  window.confirm(
                    `Retirer « ${cle.nom} » ?\n\n` +
                      (cles.length === 1
                        ? 'C’est votre seule clé : vous vous reconnecterez sans elle.'
                        : 'Vos autres clés continueront de fonctionner.'),
                  )
                ) {
                  retrait.mutate(cle.id);
                }
              }}
            />
          ))}

          {cles.length === 0 && (
            <li className="py-3 text-sm text-gray-500 dark:text-gray-400">
              Aucune clé enrôlée.
            </li>
          )}
        </ul>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => void enroler()}
          disabled={enCours}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-medium text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 min-h-[44px] disabled:opacity-50"
        >
          {enCours ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          {enCours ? 'Touchez votre clé…' : 'Ajouter une clé'}
        </button>

        {/*
          Le rappel de la seconde clé, tant qu'il n'y en a qu'une.

          C'est le moment où l'on est le plus disposé à en brancher une autre :
          la première vient d'être enrôlée, le tiroir est ouvert. Le dire plus
          tard, dans une page d'aide, revient à ne pas le dire.
        */}
        {cles.length === 1 && (
          <p className="text-sm text-amber-800 dark:text-amber-300 flex items-center gap-1.5">
            <ShieldCheck className="w-4 h-4 shrink-0" />
            Enrôlez-en une seconde et rangez-la ailleurs : une clé unique perdue, c’est un compte
            fermé.
          </p>
        )}
      </div>
    </section>
  );
}

function LigneCle({
  cle,
  occupe,
  surRenommer,
  surRetirer,
}: {
  cle: CleSecurite;
  occupe: boolean;
  surRenommer: (nom: string) => void;
  surRetirer: () => void;
}) {
  const [edition, setEdition] = useState(false);
  const [saisie, setSaisie] = useState(cle.nom);

  const valider = () => {
    const propre = saisie.trim();
    setEdition(false);
    if (propre !== '' && propre !== cle.nom) {
      surRenommer(propre);
    } else {
      setSaisie(cle.nom);
    }
  };

  const jamais = cle.dernierUsageLe === null;

  return (
    <li className="py-2 flex items-center gap-2">
      <span className="flex-1 min-w-0">
        {edition ? (
          <input
            autoFocus
            value={saisie}
            onChange={(e) => setSaisie(e.target.value)}
            onBlur={valider}
            onKeyDown={(e) => {
              if (e.key === 'Enter') valider();
              if (e.key === 'Escape') {
                setSaisie(cle.nom);
                setEdition(false);
              }
            }}
            aria-label={`Renommer « ${cle.nom} »`}
            className="w-full rounded border border-primary-300 dark:border-primary-700 bg-white dark:bg-gray-900 px-2 py-1 text-sm"
          />
        ) : (
          <span className="block text-sm text-gray-900 dark:text-gray-100">{cle.nom}</span>
        )}

        {/*
          « Jamais utilisée » n'est pas une statistique : c'est la phrase qui
          désigne la clé perdue, le jour où il faut en retirer une.
        */}
        <span className="block text-xs text-gray-500 dark:text-gray-400">
          {jamais
            ? 'Jamais encore utilisée'
            : `Dernière utilisation : ${new Date(cle.dernierUsageLe as string).toLocaleDateString('fr-FR')}`}
        </span>
      </span>

      {!edition && (
        <button
          type="button"
          onClick={() => setEdition(true)}
          aria-label={`Renommer « ${cle.nom} »`}
          className="shrink-0 inline-flex items-center justify-center w-11 h-11 sm:w-8 sm:h-8 text-gray-400 hover:text-primary-600"
        >
          <Pencil className="w-4 h-4" />
        </button>
      )}

      <button
        type="button"
        disabled={occupe}
        onClick={surRetirer}
        aria-label={`Retirer « ${cle.nom} »`}
        className="shrink-0 inline-flex items-center justify-center w-11 h-11 sm:w-8 sm:h-8 text-gray-400 hover:text-red-600 disabled:opacity-40"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </li>
  );
}
